import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import api from '../../services/api';
import {
  ChevronLeft, ChevronRight, Save, ShieldCheck,
  Clock, Calendar, Zap, Users,
  CalendarOff, CalendarCheck, Trash2, Plus, Minus
} from 'lucide-react';

// ── Helpers ──────────────────────────────────────────────────────────────────
const D_SHORT = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const D_FULL = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
  'August', 'September', 'October', 'November', 'December'];

const getDaysInMonth = (y, m) => new Date(y, m + 1, 0).getDate();
const getFirstDay = (y, m) => new Date(y, m, 1).getDay();
const toKey = (y, m, d) =>
  `${y}-${String(m + 1).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
const toDateKey = toKey;
const isToday = (y, m, d) => {
  const t = new Date();
  return t.getFullYear() === y && t.getMonth() === m && t.getDate() === d;
};
const isPast = (y, m, d) => {
  const t = new Date(); t.setHours(0, 0, 0, 0);
  return new Date(y, m, d) < t;
};
const fmtTime = t => {
  if (!t) return '';
  const [h, mi] = t.split(':');
  let hr = parseInt(h);
  const ap = hr >= 12 ? 'PM' : 'AM';
  hr = hr % 12 || 12;
  return `${hr}:${mi} ${ap}`;
};
const timeToDecimal = t => {
  if (!t) return 0;
  const [h, m] = t.split(':').map(Number);
  return h + (m || 0) / 60;
};
const defaultSlot = () => ({ startTime: '09:00', endTime: '17:00', maxSurgeries: 5 });

const getPreviousWeekDates = (completedAt, dayName) => {
  if (!completedAt) return '';
  try {
    const today = new Date(completedAt);
    today.setHours(0, 0, 0, 0);

    let startOfWeek = new Date(today);
    const day = today.getDay();
    if (day >= 3) {
      startOfWeek.setDate(today.getDate() + (7 - day));
    } else {
      startOfWeek.setDate(today.getDate() - day);
    }
    startOfWeek.setHours(0, 0, 0, 0);

    const dow = D_FULL.findIndex(f => f.toLowerCase() === dayName.toLowerCase());
    if (dow === -1) return '';
    const targetDate = new Date(startOfWeek);
    targetDate.setDate(startOfWeek.getDate() + dow);

    const dayNum = targetDate.getDate();
    const month = MONTHS[targetDate.getMonth()].slice(0, 3);
    return `${month} ${dayNum}`;
  } catch (e) {
    return '';
  }
};

// ── Mini Calendar Component ───────────────────────────────────────────────────
function MiniCalendar({ selectedDates, offDays = new Set(), slotMap = {}, onDateClick, isDateDisabled }) {
  const todayObj = new Date();
  const [cur, setCur] = useState({ y: todayObj.getFullYear(), m: todayObj.getMonth() });

  const prev = () => setCur(c => c.m === 0 ? { y: c.y - 1, m: 11 } : { y: c.y, m: c.m - 1 });
  const next = () => setCur(c => c.m === 11 ? { y: c.y + 1, m: 0 } : { y: c.y, m: c.m + 1 });

  const totalDays = getDaysInMonth(cur.y, cur.m);
  const firstDay = getFirstDay(cur.y, cur.m);
  const cells = Array(firstDay).fill(null).concat(
    Array.from({ length: totalDays }, (_, i) => i + 1)
  );

  return (
    <div className="bg-white rounded-3xl border border-gray-150 shadow-lg overflow-hidden">
      {/* Month nav */}
      <div className="flex items-center justify-between px-6 py-4 bg-gradient-to-r from-[#143E78] to-[#1a52a0]">
        <button type="button" onClick={prev} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-all">
          <ChevronLeft size={16} />
        </button>
        <span className="text-white font-black text-sm tracking-widest uppercase">
          {MONTHS[cur.m]} {cur.y}
        </span>
        <button type="button" onClick={next} className="w-8 h-8 flex items-center justify-center rounded-full bg-white/10 hover:bg-white/20 text-white transition-all">
          <ChevronRight size={16} />
        </button>
      </div>

      {/* Day headers */}
      <div className="grid grid-cols-7 border-b border-gray-50">
        {D_SHORT.map(d => (
          <div key={d} className="py-2 text-center text-[10px] font-black text-gray-400 uppercase tracking-wider">
            {d}
          </div>
        ))}
      </div>

      {/* Cells */}
      <div className="grid grid-cols-7 p-3 gap-1">
        {cells.map((day, i) => {
          if (!day) return <div key={`e-${i}`} />;
          const past = isPast(cur.y, cur.m, day);
          const todayCell = isToday(cur.y, cur.m, day);
          const dateKey = toDateKey(cur.y, cur.m, day);
          const isDisabled = past || (isDateDisabled && isDateDisabled(dateKey));
          const selected = selectedDates.has(dateKey);
          const isOff = offDays.has(dateKey);
          const slot = slotMap[dateKey];
          const dow = new Date(cur.y, cur.m, day).getDay();

          return (
            <button
              key={dateKey}
              type="button"
              onClick={() => !isDisabled && onDateClick(dateKey)}
              disabled={isDisabled}
              title={selected && slot
                ? `${D_FULL[dow]} ${day}: ${fmtTime(slot.startTime)} – ${fmtTime(slot.endTime)}`
                : `${D_FULL[dow]}, ${day} ${MONTHS[cur.m]}`}
              className={`
                relative aspect-square flex flex-col items-center justify-center rounded-xl text-xs font-bold transition-all
                ${isDisabled ? 'opacity-55 cursor-not-allowed bg-slate-100 text-slate-500 font-medium' : 'cursor-pointer hover:scale-105'}
                ${todayCell ? 'ring-2 ring-[#143E78] ring-offset-1 border-2 border-[#143E78] font-black' : ''}
                ${selected && isOff && !isDisabled
                  ? 'bg-amber-100 text-amber-800 border-2 border-amber-300 shadow-sm'
                  : selected && !isDisabled
                    ? 'bg-gradient-to-br from-emerald-500 to-emerald-600 text-white shadow-md shadow-emerald-200'
                    : !isDisabled
                      ? 'bg-slate-200/80 text-slate-700 hover:bg-blue-100 hover:text-[#143E78]'
                      : ''}
              `}
            >
              <span>{day}</span>
              {selected && !isDisabled && (
                <span className={`absolute bottom-0.5 left-1/2 -translate-x-1/2 w-1 h-1 rounded-full ${isOff ? 'bg-amber-400' : 'bg-white/70'}`} />
              )}
            </button>
          );
        })}
      </div>

      {/* Legend */}
      <div className="flex items-center gap-4 px-4 pb-4 pt-1">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-500" />
          <span className="text-[10px] font-bold text-gray-400">Available</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-amber-100 border border-amber-200" />
          <span className="text-[10px] font-bold text-gray-400">Day Off</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full bg-gray-100 border border-gray-200" />
          <span className="text-[10px] font-bold text-gray-400">Unselected</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded-full border-2 border-[#143E78]" />
          <span className="text-[10px] font-bold text-gray-400">Today</span>
        </div>
      </div>
    </div>
  );
}

// ── Date Slot Card Component ─────────────────────────────────────────────────
function DateSlotCard({ dateKey, slot, onRemove, onChange, onToggleOff, isFirst, isOff }) {
  const d = new Date(dateKey + 'T00:00:00');
  const dow = d.getDay();
  const day = d.getDate();
  const mon = MONTHS[d.getMonth()].slice(0, 3);
  const yr = d.getFullYear();

  return (
    <div className={`rounded-2xl border-2 overflow-hidden transition-all duration-300 hover:shadow-md
      ${isOff
        ? 'border-slate-100 bg-slate-50/40 opacity-75'
        : isFirst
          ? 'border-indigo-400/40 shadow-md shadow-indigo-100/30 bg-white ring-1 ring-indigo-500/5'
          : 'border-slate-100 shadow-sm bg-white'}`}>

      <style>{`
        .custom-time-input::-webkit-calendar-picker-indicator {
          background: transparent;
          bottom: 0;
          color: transparent;
          cursor: pointer;
          height: auto;
          left: 0;
          position: absolute;
          right: 0;
          top: 0;
          width: auto;
        }
      `}</style>

      <div className="flex flex-col sm:flex-row sm:items-center gap-4 px-6 py-5">
        {/* Date badge */}
        <div className={`w-14 h-15 rounded-xl border flex flex-col overflow-hidden items-center justify-center flex-shrink-0 transition-all duration-300 shadow-sm
          ${isOff
            ? 'bg-slate-50 border-slate-200 text-slate-400 opacity-60'
            : isFirst
              ? 'bg-white border-indigo-200 ring-2 ring-indigo-500/10'
              : 'bg-white border-slate-200'}`}
        >
          <div className={`w-full py-0.5 text-center text-[8px] font-black uppercase tracking-wider
            ${isOff
              ? 'bg-slate-200 text-slate-500'
              : isFirst
                ? 'bg-gradient-to-r from-indigo-600 to-indigo-500 text-white'
                : 'bg-[#143E78] text-white'}`}
          >
            {mon}
          </div>
          <div className={`flex-1 flex items-center justify-center w-full font-black text-lg py-1
            ${isOff ? 'text-slate-400' : 'text-[#143E78]'}`}
          >
            {day}
          </div>
        </div>

        <div className="flex-1">
          <div className="flex items-center gap-2 mb-3">
            <p className={`font-black text-sm tracking-tight ${isOff ? 'text-slate-400' : 'text-slate-800'}`}>
              {D_FULL[dow]}, {day} {MONTHS[d.getMonth()]} {yr}
            </p>
            {isFirst && !isOff && (
              <span className="text-[9px] font-extrabold bg-indigo-50 border border-indigo-150 text-indigo-600 px-2 py-0.5 rounded-full uppercase tracking-wider shadow-sm">
                Start Date
              </span>
            )}
            {isOff && (
              <span className="text-[9px] font-extrabold bg-slate-100 border border-slate-200 text-slate-400 px-2 py-0.5 rounded-full uppercase tracking-wider shadow-sm">
                Day Off
              </span>
            )}
          </div>

          {isOff ? (
            <p className="text-xs text-slate-400 italic font-medium">No surgeries scheduled for this day.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Start Time Field */}
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5 flex items-center gap-1">
                  <Clock size={10} className="text-slate-400" /> Start Time
                </p>
                <div className="relative flex items-center bg-slate-50 border border-slate-200 rounded-xl hover:border-slate-300 focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-indigo-500 transition-all overflow-hidden">
                  <input
                    type="time"
                    value={slot.startTime}
                    onChange={e => onChange('startTime', e.target.value)}
                    className="custom-time-input w-full bg-transparent border-0 px-3 py-2 text-sm font-bold text-slate-700 focus:outline-none cursor-pointer"
                  />
                  <div className="absolute right-3 pointer-events-none text-slate-400">
                    <Clock size={14} />
                  </div>
                </div>
              </div>

              {/* End Time Field */}
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5 flex items-center gap-1">
                  <Clock size={10} className="text-slate-400" /> End Time
                </p>
                <div className="relative flex items-center bg-slate-50 border border-slate-200 rounded-xl hover:border-slate-300 focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-indigo-500 transition-all overflow-hidden">
                  <input
                    type="time"
                    value={slot.endTime}
                    onChange={e => onChange('endTime', e.target.value)}
                    className="custom-time-input w-full bg-transparent border-0 px-3 py-2 text-sm font-bold text-slate-700 focus:outline-none cursor-pointer"
                  />
                  <div className="absolute right-3 pointer-events-none text-slate-400">
                    <Clock size={14} />
                  </div>
                </div>
              </div>

              {/* Max Cases Stepper Field */}
              <div>
                <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1.5 flex items-center gap-1">
                  <Users size={10} className="text-slate-400" /> Max Cases
                </p>
                <div className="flex items-center border border-slate-200 bg-slate-50 rounded-xl overflow-hidden focus-within:ring-2 focus-within:ring-indigo-500/20 focus-within:border-indigo-500 hover:border-slate-300 transition-all h-[38px]">
                  <button
                    type="button"
                    disabled={slot.maxSurgeries <= 1}
                    onClick={() => onChange('maxSurgeries', Math.max(1, slot.maxSurgeries - 1))}
                    className="px-3 h-full flex items-center justify-center text-slate-500 hover:bg-slate-200/50 active:bg-slate-200 transition-all font-bold disabled:opacity-30 disabled:pointer-events-none"
                  >
                    <Minus size={12} />
                  </button>
                  <input
                    type="number"
                    min="1"
                    max="20"
                    value={slot.maxSurgeries}
                    onChange={e => onChange('maxSurgeries', Math.max(1, Math.min(20, parseInt(e.target.value) || 1)))}
                    className="w-full text-center bg-transparent border-0 text-slate-700 font-bold text-sm focus:outline-none [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                  />
                  <button
                    type="button"
                    disabled={slot.maxSurgeries >= 20}
                    onClick={() => onChange('maxSurgeries', Math.min(20, slot.maxSurgeries + 1))}
                    className="px-3 h-full flex items-center justify-center text-slate-500 hover:bg-slate-200/50 active:bg-slate-200 transition-all font-bold disabled:opacity-30 disabled:pointer-events-none"
                  >
                    <Plus size={12} />
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Actions Row */}
        <div className="flex flex-row sm:flex-col items-center gap-2 flex-shrink-0 sm:self-center">
          {/* Toggle off/on */}
          <button
            type="button"
            onClick={onToggleOff}
            title={isOff ? 'Mark as available' : 'Mark as day off'}
            className={`px-3 py-2 rounded-xl text-xs font-black transition-all border flex items-center gap-1.5 shadow-sm active:scale-95 w-full justify-center
              ${isOff
                ? 'bg-emerald-50 hover:bg-emerald-100 border-emerald-200 text-emerald-700'
                : 'bg-amber-50 hover:bg-amber-100 border-amber-200 text-amber-700'}`}
          >
            {isOff ? (
              <>
                <CalendarCheck size={14} />
                <span>Activate</span>
              </>
            ) : (
              <>
                <CalendarOff size={14} />
                <span>Day Off</span>
              </>
            )}
          </button>

          {/* Remove from range entirely */}
          <button
            type="button"
            onClick={onRemove}
            title="Remove from range"
            className="w-9 h-9 flex items-center justify-center rounded-xl bg-slate-50 hover:bg-red-50 border border-slate-200 hover:border-red-200 text-slate-400 hover:text-red-650 active:scale-95 shadow-sm transition-all sm:w-full sm:h-9"
          >
            <Trash2 size={14} />
          </button>
        </div>
      </div>

      {/* Time bar — only when active */}
      {!isOff && (() => {
        const startDec = timeToDecimal(slot.startTime);
        const endDec = timeToDecimal(slot.endTime);
        const duration = Math.max(0, endDec - startDec);

        const leftPct = (startDec / 24) * 100;
        const widthPct = (duration / 24) * 100;

        return (
          <div className="px-6 pb-4 pt-2 border-t border-slate-100 bg-slate-50/20">
            {/* 24h Visual Grid */}
            <div className="relative h-4 mt-2">
              <div className="absolute inset-0 h-1.5 bg-slate-100 rounded-full top-1/2 -translate-y-1/2" />

              <div className="absolute left-[25%] top-1/2 -translate-y-1/2 w-[1px] h-2 bg-slate-300" />
              <div className="absolute left-[50%] top-1/2 -translate-y-1/2 w-[1px] h-3 bg-slate-400" />
              <div className="absolute left-[75%] top-1/2 -translate-y-1/2 w-[1px] h-2 bg-slate-300" />

              <div
                className="absolute h-2.5 rounded-full bg-gradient-to-r from-indigo-650 via-indigo-500 to-cyan-550 shadow-md top-1/2 -translate-y-1/2 transition-all duration-500"
                style={{
                  left: `${leftPct}%`,
                  width: `${widthPct}%`
                }}
              >
                <div className="absolute -left-1 -top-0.5 w-3.5 h-3.5 rounded-full bg-white border-2 border-indigo-650 shadow-sm" />
                <div className="absolute -right-1 -top-0.5 w-3.5 h-3.5 rounded-full bg-white border-2 border-cyan-550 shadow-sm" />
              </div>
            </div>

            {/* Grid labels */}
            <div className="relative flex justify-between text-[9px] font-black tracking-wider text-slate-300 uppercase select-none mt-1">
              <span>12 AM</span>
              <span>6 AM</span>
              <span>12 PM</span>
              <span>6 PM</span>
              <span>12 AM</span>
            </div>

            {/* Floating details */}
            <div className="flex justify-between items-center mt-3 pt-2.5 border-t border-dashed border-slate-100">
              <span className="text-[10px] font-bold text-slate-400">Scheduled Operating Window</span>
              <div className="flex items-center gap-1.5">
                <span className="text-[11px] font-extrabold text-indigo-650 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded-lg shadow-sm">
                  {fmtTime(slot.startTime)}
                </span>
                <span className="text-slate-300 font-bold text-[10px]">to</span>
                <span className="text-[11px] font-extrabold text-cyan-650 bg-cyan-50 border border-cyan-100 px-2 py-0.5 rounded-lg shadow-sm">
                  {fmtTime(slot.endTime)}
                </span>
                <span className="text-slate-300 font-bold ml-1 text-[10px]">•</span>
                <span className="text-[11px] font-extrabold text-slate-600 bg-slate-100 border border-slate-200 px-2 py-0.5 rounded-lg shadow-sm ml-1">
                  {duration.toFixed(1).replace('.0', '')} hrs
                </span>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ── Weekly Day Card ───────────────────────────────────────────────────────────
function WeeklyDayCard({ dayPattern, onChange }) {
  const { day, isAvailable, startTime, endTime } = dayPattern;

  return (
    <div className={`p-4 sm:p-6 rounded-[20px] sm:rounded-[30px] border transition-all duration-300 ${isAvailable
        ? 'border-blue-100 bg-white shadow-sm'
        : 'border-slate-100 bg-slate-50/50'
      }`}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 sm:gap-4">
        {/* Toggle & Day Label */}
        <div className="flex items-center gap-3 sm:gap-4 min-w-0 sm:min-w-[200px]">
          <button
            type="button"
            onClick={() => onChange(day, 'isAvailable', !isAvailable)}
            className={`relative w-12 h-6 sm:w-14 sm:h-7 rounded-full transition-colors duration-300 focus:outline-none ${isAvailable ? 'bg-[#143E78]' : 'bg-slate-200'
              }`}
          >
            <span className={`absolute top-[2px] sm:top-1 w-5 h-5 rounded-full bg-white shadow transition-all duration-300 ${isAvailable ? 'left-[26px] sm:left-8' : 'left-1'
              }`} />
          </button>
          <span className={`font-black text-base sm:text-lg tracking-tight ${isAvailable ? 'text-[#143E78]' : 'text-slate-400 font-bold'
            }`}>
            {day}
          </span>
        </div>

        {/* Inputs or Off-duty message */}
        {isAvailable ? (
          <div className="flex-1 grid grid-cols-2 gap-3 sm:gap-4 max-w-lg mt-1 sm:mt-0">
            {/* Start Time */}
            <div>
              <p className="text-[9px] sm:text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1 sm:mb-1.5 flex items-center gap-1">
                <Clock size={10} className="text-slate-400" /> Start Time
              </p>
              <div className="relative flex items-center bg-slate-50 border border-slate-200 rounded-xl hover:border-slate-350 focus-within:ring-2 focus-within:ring-[#143E78]/10 focus-within:border-[#143E78] transition-all overflow-hidden">
                <input
                  type="time"
                  value={startTime}
                  onChange={e => onChange(day, 'startTime', e.target.value)}
                  className="custom-time-input w-full bg-transparent border-0 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-bold text-slate-700 focus:outline-none cursor-pointer"
                />
                <div className="absolute right-2 sm:right-3 pointer-events-none text-slate-400">
                  <Clock size={12} className="sm:w-3.5 sm:h-3.5" />
                </div>
              </div>
            </div>

            {/* End Time */}
            <div>
              <p className="text-[9px] sm:text-[10px] font-bold text-slate-400 uppercase tracking-widest mb-1 sm:mb-1.5 flex items-center gap-1">
                <Clock size={10} className="text-slate-400" /> End Time
              </p>
              <div className="relative flex items-center bg-slate-50 border border-slate-200 rounded-xl hover:border-slate-350 focus-within:ring-2 focus-within:ring-[#143E78]/10 focus-within:border-[#143E78] transition-all overflow-hidden">
                <input
                  type="time"
                  value={endTime}
                  onChange={e => onChange(day, 'endTime', e.target.value)}
                  className="custom-time-input w-full bg-transparent border-0 px-2 sm:px-3 py-1.5 sm:py-2 text-xs sm:text-sm font-bold text-slate-700 focus:outline-none cursor-pointer"
                />
                <div className="absolute right-2 sm:right-3 pointer-events-none text-slate-400">
                  <Clock size={12} className="sm:w-3.5 sm:h-3.5" />
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="flex-1 py-1 sm:py-2 flex items-center">
            <p className="text-[11px] sm:text-sm text-slate-400 italic font-medium leading-snug">Off-duty. No surgeries will be assigned for this day.</p>
          </div>
        )}
      </div>
    </div>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
const DoctorAvailabilityPublic = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');

  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  // dateSlots: { [YYYY-MM-DD]: { startTime, endTime, maxSurgeries } }
  const [dateSlots, setDateSlots] = useState({});
  const [offDays, setOffDays] = useState(new Set());

  const selectedDates = new Set(Object.keys(dateSlots));

  const [activeTab, setActiveTab] = useState('schedule');

  const [weeklyPattern, setWeeklyPattern] = useState([
    { day: 'Monday', isAvailable: false, startTime: '09:00', endTime: '17:00' },
    { day: 'Tuesday', isAvailable: false, startTime: '09:00', endTime: '17:00' },
    { day: 'Wednesday', isAvailable: false, startTime: '09:00', endTime: '17:00' },
    { day: 'Thursday', isAvailable: false, startTime: '09:00', endTime: '17:00' },
    { day: 'Friday', isAvailable: false, startTime: '09:00', endTime: '17:00' },
    { day: 'Saturday', isAvailable: false, startTime: '09:00', endTime: '17:00' },
    { day: 'Sunday', isAvailable: false, startTime: '09:00', endTime: '17:00' },
  ]);

  const [previousSchedule, setPreviousSchedule] = useState(null);
  const [previousCompletedAt, setPreviousCompletedAt] = useState(null);

  const getActiveWeekBounds = () => {
    const dates = Object.keys(dateSlots || {}).map(d => new Date(d + 'T00:00:00'));
    let maxDate = null;
    if (dates.length > 0) {
      maxDate = new Date(Math.max(...dates));
      maxDate.setHours(0, 0, 0, 0);
    }

    const today = new Date();
    today.setHours(0, 0, 0, 0);

    let startOfWeek = new Date();

    if (maxDate && today <= maxDate) {
      // Lock active week to the Sunday of the week containing maxDate
      startOfWeek = new Date(maxDate);
      startOfWeek.setDate(maxDate.getDate() - maxDate.getDay());
    } else if (maxDate && today > maxDate) {
      // The chosen dates have passed, shift to next Sunday after maxDate
      const maxDateSunday = new Date(maxDate);
      maxDateSunday.setDate(maxDate.getDate() - maxDate.getDay());
      startOfWeek = new Date(maxDateSunday);
      startOfWeek.setDate(maxDateSunday.getDate() + 7);
    } else {
      // Standard dynamic fallback
      const day = today.getDay();
      startOfWeek = new Date(today);
      if (day >= 3) {
        startOfWeek.setDate(today.getDate() + (7 - day));
      } else {
        startOfWeek.setDate(today.getDate() - day);
      }
    }

    startOfWeek.setHours(0, 0, 0, 0);

    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6); // Saturday
    endOfWeek.setHours(23, 59, 59, 999);

    return { startOfWeek, endOfWeek };
  };

  const isDateDisabled = (dateKey) => {
    const cellDate = new Date(dateKey + 'T00:00:00');
    const { startOfWeek, endOfWeek } = getActiveWeekBounds();
    return cellDate < startOfWeek || cellDate > endOfWeek;
  };

  const getDateKeyForDay = (dayName) => {
    const { startOfWeek } = getActiveWeekBounds();
    const dow = D_FULL.findIndex(f => f.toLowerCase() === dayName.toLowerCase());
    if (dow === -1) return null;
    const targetDate = new Date(startOfWeek);
    targetDate.setDate(startOfWeek.getDate() + dow);
    return toDateKey(targetDate.getFullYear(), targetDate.getMonth(), targetDate.getDate());
  };

  const getDayNameFromDateKey = (dateKey) => {
    const d = new Date(dateKey + 'T00:00:00');
    return D_FULL[d.getDay()];
  };

  const syncWeeklyPatternForDate = (dateKey, isAvailable, startTime = null, endTime = null) => {
    const dayName = getDayNameFromDateKey(dateKey);
    setWeeklyPattern(prev => prev.map(p => {
      if (p.day.toLowerCase() === dayName.toLowerCase()) {
        return {
          ...p,
          isAvailable,
          ...(startTime ? { startTime } : {}),
          ...(endTime ? { endTime } : {})
        };
      }
      return p;
    }));
  };

  const handleWeeklyPatternChange = (dayName, field, value) => {
    // 1. Update weekly pattern
    setWeeklyPattern(prev => prev.map(p => {
      if (p.day === dayName) {
        return { ...p, [field]: value };
      }
      return p;
    }));

    // 2. Synchronize with calendar date slots
    const dateKey = getDateKeyForDay(dayName);
    if (!dateKey) return;

    if (field === 'isAvailable') {
      if (value) {
        // Toggle on: Add to dateSlots, remove from offDays
        const pattern = weeklyPattern.find(p => p.day === dayName);
        setDateSlots(prev => ({
          ...prev,
          [dateKey]: {
            startTime: pattern?.startTime || '09:00',
            endTime: pattern?.endTime || '17:00',
            maxSurgeries: 5
          }
        }));
        setOffDays(prev => {
          const next = new Set(prev);
          next.delete(dateKey);
          return next;
        });
      } else {
        // Toggle off: Remove from dateSlots and offDays
        setDateSlots(prev => {
          const next = { ...prev };
          delete next[dateKey];
          return next;
        });
        setOffDays(prev => {
          const next = new Set(prev);
          next.delete(dateKey);
          return next;
        });
      }
    } else if (field === 'startTime' || field === 'endTime') {
      // Update time slots if that day is currently active in dateSlots
      setDateSlots(prev => {
        if (!prev[dateKey]) return prev;
        return {
          ...prev,
          [dateKey]: {
            ...prev[dateKey],
            [field]: value
          }
        };
      });
    }
  };

  const buildWeeklyPatternPayload = () => {
    return weeklyPattern.map(p => ({
      day: p.day,
      isAvailable: p.isAvailable,
      timeSlots: p.isAvailable ? [{ startTime: p.startTime, endTime: p.endTime, maxSurgeries: 5 }] : []
    }));
  };

  const loadWeeklyPattern = (dbSchedule) => {
    if (!dbSchedule || !dbSchedule.length) return;
    const mapped = D_FULL.map(day => {
      const dbDay = dbSchedule.find(d => d.day.toLowerCase() === day.toLowerCase());
      if (dbDay) {
        return {
          day,
          isAvailable: dbDay.isAvailable,
          startTime: dbDay.timeSlots?.[0]?.startTime || '09:00',
          endTime: dbDay.timeSlots?.[0]?.endTime || '17:00'
        };
      }
      return { day, isAvailable: false, startTime: '09:00', endTime: '17:00' };
    });
    setWeeklyPattern(mapped);
  };

  const fetchAvailability = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/doctor-availability/public-details?token=${token}`);
      const data = res.data.data;
      if (data?.weeklySchedule && data.weeklySchedule.some(d => d.isAvailable)) {
        setPreviousSchedule(data.weeklySchedule);
        setPreviousCompletedAt(data.completedAt || data.updatedAt || null);
        setActiveTab('upcoming');
      } else {
        setActiveTab('schedule');
      }
    } catch (e) {
      toast.error('Failed to load existing availability schedule');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!token) {
      toast.error('Invalid or expired availability link access', { id: 'token-missing-toast' });
    } else {
      fetchAvailability();
    }
  }, [token]);

  // Calendar click handler: 3-way toggle (Unselected -> Active -> Day Off -> Unselected)
  const handleDateClick = (dateKey) => {
    const isSelected = selectedDates.has(dateKey);
    const isOff = offDays.has(dateKey);

    if (!isSelected) {
      // Unselected -> Active (green)
      if (selectedDates.size >= 7) {
        toast.error("Maximum 7 days allowed. Remove a date first.", { icon: '🚫', duration: 3000 });
        return;
      }
      setDateSlots(prev => ({ ...prev, [dateKey]: defaultSlot() }));
      setOffDays(prev => { const next = new Set(prev); next.delete(dateKey); return next; });
      syncWeeklyPatternForDate(dateKey, true);
    } else if (!isOff) {
      // Active (green) -> Day Off (amber)
      setOffDays(prev => { const next = new Set(prev); next.add(dateKey); return next; });
      syncWeeklyPatternForDate(dateKey, false);
    } else {
      // Day Off (amber) -> Unselected (white)
      setDateSlots(prev => { const next = { ...prev }; delete next[dateKey]; return next; });
      setOffDays(prev => { const next = new Set(prev); next.delete(dateKey); return next; });
      syncWeeklyPatternForDate(dateKey, false);
    }
  };

  const handleToggleOff = (dateKey) => {
    const isCurrentlyOff = offDays.has(dateKey);
    setOffDays(prev => {
      const next = new Set(prev);
      if (isCurrentlyOff) {
        next.delete(dateKey);
      } else {
        next.add(dateKey);
      }
      return next;
    });
    syncWeeklyPatternForDate(dateKey, isCurrentlyOff);
  };

  const handleSlotChange = (dateKey, field, value) => {
    setDateSlots(prev => ({
      ...prev,
      [dateKey]: { ...prev[dateKey], [field]: value }
    }));
    if (field === 'startTime' || field === 'endTime') {
      syncWeeklyPatternForDate(dateKey, true, field === 'startTime' ? value : null, field === 'endTime' ? value : null);
    }
  };

  const handleRemoveDate = (dateKey) => {
    setDateSlots(prev => {
      const next = { ...prev };
      delete next[dateKey];
      return next;
    });
    setOffDays(prev => {
      const next = new Set(prev);
      next.delete(dateKey);
      return next;
    });
    syncWeeklyPatternForDate(dateKey, false);
  };

  const sortedDateKeys = Object.keys(dateSlots).sort();
  const activeDateKeys = sortedDateKeys.filter(k => !offDays.has(k));

  const totalHours = activeDateKeys.reduce((acc, k) => {
    const s = dateSlots[k];
    return acc + Math.max(0, timeToDecimal(s.endTime) - timeToDecimal(s.startTime));
  }, 0);

  const totalCases = activeDateKeys.reduce((acc, k) => acc + (dateSlots[k]?.maxSurgeries || 0), 0);

  // Re-build schedule to weekday groupings format for backend
  const buildSchedulePayload = () => {
    const byDay = {};
    Object.entries(dateSlots).forEach(([key, slot]) => {
      if (offDays.has(key)) return; // skip off days
      const dayName = D_FULL[new Date(key + 'T00:00:00').getDay()];
      if (!byDay[dayName]) byDay[dayName] = slot;
    });
    return D_FULL.map(day => ({
      day,
      isAvailable: !!byDay[day],
      timeSlots: byDay[day] ? [byDay[day]] : []
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token) return toast.error('Invalid link access');

    if (activeTab === 'upcoming') {
      setActiveTab('schedule');
      return;
    }

    const hasAnyWeeklyActive = weeklyPattern.some(p => p.isAvailable);
    if (!hasAnyWeeklyActive) {
      return toast.error('Please configure at least one active day in your weekly schedule');
    }

    setLoading(true);
    try {
      const payload = buildWeeklyPatternPayload();

      await api.post(`/doctor-availability/submit-public`, {
        token,
        durationDays: "7",
        weeklySchedule: payload
      });

      setSubmitted(true);
      toast.success('Availability submitted successfully!');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Submission failed');
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-4 font-sans">
        <div className="bg-white p-10 rounded-3xl shadow-2xl border border-blue-100 max-w-md text-center">
          <div className="w-20 h-20 bg-emerald-100 text-emerald-600 rounded-full flex items-center justify-center mx-auto mb-6 text-4xl font-bold">✓</div>
          <h1 className="text-3xl font-black text-[#143E78] mb-4">Thank You, Doctor!</h1>
          <p className="text-gray-500 font-medium">Your availability has been successfully submitted. Our team will coordinate your surgery slots accordingly.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-12 px-4 font-sans">
      <div className="max-w-6xl mx-auto bg-white rounded-3xl shadow-2xl border border-blue-50 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#143E78] to-[#1a52a0] p-6 sm:p-8 text-white">
          <div className="flex flex-col sm:flex-row sm:justify-between items-start sm:items-center gap-3 sm:gap-0 mb-4 sm:mb-2">
            <div className="flex items-center gap-2 bg-blue-400/20 text-blue-200 px-3 py-1.5 sm:py-1 rounded-full text-[10px] sm:text-xs font-bold tracking-widest uppercase">
              <ShieldCheck size={14} className="text-blue-300 w-3 h-3 sm:w-3.5 sm:h-3.5" />
              <span>Verified Surgeon Portal</span>
            </div>
            <h2 className="text-[10px] sm:text-xs font-black uppercase opacity-60 tracking-wider">Surgery Operations</h2>
          </div>
          <h1 className="text-2xl sm:text-3xl font-black mb-2 leading-tight">Doctor Availability Form</h1>
          <p className="text-sm sm:text-base text-blue-100 opacity-80 font-medium">Please provide your preferred surgery slots to help us plan the case load.</p>
        </div>

        {!token && (
          <div className="bg-amber-50 border-b border-amber-100 px-8 py-4 flex items-center gap-3 text-amber-800 text-sm font-bold">
            <span className="text-lg">⚠</span>
            <span>This form was accessed without a secure token. Submissions will be disabled. Please use the link sent in your email/WhatsApp.</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="p-4 sm:p-8 space-y-6">
          {/* Stats Bar */}
          <div className="grid grid-cols-3 gap-2 sm:gap-4">
            {[
              { label: 'Selected Days', value: `${activeDateKeys.length} / ${sortedDateKeys.length}`, sub: 'active of total', icon: Calendar, color: 'blue' },
              { label: 'Total Hours', value: `${totalHours.toFixed(1).replace('.0', '')}h`, sub: 'operating time', icon: Clock, color: 'emerald' },
              { label: 'Max Cases', value: totalCases, sub: 'across all dates', icon: Zap, color: 'violet' },
            ].map(({ label, value, sub, icon: Icon, color }) => (
              <div key={label} className="bg-white rounded-2xl border border-gray-150 shadow-sm p-2 sm:p-4 flex flex-col sm:flex-row items-center justify-center sm:justify-start gap-1 sm:gap-4 text-center sm:text-left">
                <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center flex-shrink-0
                  ${color === 'blue' ? 'bg-blue-50 text-blue-600'
                    : color === 'emerald' ? 'bg-emerald-50 text-emerald-600'
                      : 'bg-violet-50 text-violet-600'}`}>
                  <Icon size={18} className="w-4 h-4 sm:w-auto sm:h-auto" />
                </div>
                <div className="w-full">
                  <p className="text-[13px] sm:text-xl font-black text-gray-900 leading-none mt-1 sm:mt-0">{value}</p>
                  <p className="text-[8px] sm:text-[10px] font-bold text-gray-400 uppercase tracking-tight mt-0.5 leading-tight truncate px-1">{label}</p>
                  <p className="hidden sm:block text-[9px] text-gray-300 font-medium">{sub}</p>
                </div>
              </div>
            ))}
          </div>

          {/* Tabs */}
          <div className="flex gap-1 bg-gray-100 p-1 rounded-2xl w-full sm:w-fit">
            {[
              { key: 'schedule', label: 'Weekly Schedule', icon: Calendar },
              { key: 'upcoming', label: "Previous Week's Schedule", icon: Clock },
            ].map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                type="button"
                onClick={() => setActiveTab(key)}
                className={`flex-1 sm:flex-none flex justify-center items-center gap-1.5 sm:gap-2 px-1 sm:px-5 py-2 rounded-xl text-[9px] sm:text-xs font-black transition-all leading-tight ${activeTab === key
                  ? 'bg-white text-[#143E78] shadow-sm'
                  : 'text-gray-400 hover:text-gray-600'
                  }`}
              >
                <Icon size={12} className="sm:w-[13px] sm:h-[13px]" />
                <span className="text-center">{label}</span>
              </button>
            ))}
          </div>

          {/* Weekly Schedule Tab content */}
          {activeTab === 'schedule' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 pt-2">
              {/* Left Column: Weekly Pattern */}
              <div className="lg:col-span-2 space-y-4">
                <div className="space-y-4">
                  {weeklyPattern.map((p) => (
                    <WeeklyDayCard
                      key={p.day}
                      dayPattern={p}
                      onChange={handleWeeklyPatternChange}
                    />
                  ))}
                </div>
              </div>

              {/* Right Column: Calendar & Override info */}
              <div className="space-y-4">
                <MiniCalendar
                  selectedDates={selectedDates}
                  offDays={offDays}
                  slotMap={dateSlots}
                  onDateClick={handleDateClick}
                  isDateDisabled={isDateDisabled}
                />

                <div className="rounded-3xl p-5 border bg-blue-50 border-blue-100">
                  <p className="text-[10px] font-black uppercase tracking-widest mb-3 text-blue-655">
                    Weekly Overrides
                  </p>
                  <p className="text-[11px] font-medium leading-relaxed text-blue-750">
                    Use the calendar to set specific date overrides for upcoming days. Tap a selected day to set a temporary Day Off.
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Previous Week's Schedule Tab content */}
          {activeTab === 'upcoming' && (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 pt-2">
              {/* Left Column: Previous Week's Schedule Display */}
              <div className="lg:col-span-2 space-y-4">
                {previousSchedule && previousSchedule.some(d => d.isAvailable) ? (
                  <div className="bg-white rounded-3xl border border-gray-150 shadow-sm p-8 space-y-6">
                    <div className="flex items-center gap-3 text-indigo-900 border-b border-gray-100 pb-4">
                      <div className="w-10 h-10 rounded-xl bg-indigo-50 text-indigo-600 flex items-center justify-center">
                        <Calendar size={20} />
                      </div>
                      <div>
                        <h3 className="text-lg font-black tracking-tight text-slate-800">Previous Week's Selection</h3>
                        <p className="text-xs font-semibold text-slate-450">Submitted on {previousCompletedAt ? new Date(previousCompletedAt).toLocaleDateString('en-US', { dateStyle: 'medium' }) : 'N/A'}</p>
                      </div>
                    </div>

                    <p className="text-sm font-medium text-slate-500 leading-relaxed">
                      This is the reference record of the availability you submitted for your previous week. To configure and submit your schedule for this upcoming week, please switch to the <strong className="text-[#143E78]">Weekly Schedule</strong> tab.
                    </p>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
                      {previousSchedule
                        .filter(d => d.isAvailable)
                        .map(d => {
                          const dateStr = getPreviousWeekDates(previousCompletedAt, d.day);
                          return (
                            <div key={d.day} className="flex items-center justify-between bg-slate-50/50 border border-slate-100 rounded-2xl p-5 shadow-xs transition-all hover:shadow-sm">
                              <div>
                                <span className="text-sm font-black text-slate-800 block">
                                  {d.day}
                                </span>
                                {dateStr && (
                                  <span className="text-[11px] font-bold text-slate-400">
                                    {dateStr}
                                  </span>
                                )}
                              </div>
                              <span className="text-xs font-extrabold text-indigo-655 bg-indigo-50 border border-indigo-100 px-3 py-1.5 rounded-xl shadow-xs">
                                {fmtTime(d.timeSlots?.[0]?.startTime || '09:00')} - {fmtTime(d.timeSlots?.[0]?.endTime || '17:00')}
                              </span>
                            </div>
                          );
                        })}
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-3xl border border-gray-150 shadow-sm p-12 text-center">
                    <div className="w-16 h-16 bg-blue-50 text-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Calendar size={28} />
                    </div>
                    <h3 className="text-xl font-black text-slate-800 mb-2">No Previous Schedule Found</h3>
                    <p className="text-slate-400 font-medium text-sm max-w-md mx-auto mb-6">
                      We couldn't find a record of your previous week's availability. Please use the tab below to select your slots.
                    </p>
                    <button
                      type="button"
                      onClick={() => setActiveTab('schedule')}
                      className="px-6 py-2.5 bg-[#143E78] hover:bg-[#0f2c56] text-white rounded-xl text-xs font-black shadow-md transition-all active:scale-95 cursor-pointer"
                    >
                      Configure Weekly Availability
                    </button>
                  </div>
                )}
              </div>

              {/* Right Column: Calendar for context only (read-only) */}
              <div className="space-y-4">
                <MiniCalendar
                  selectedDates={new Set()}
                  offDays={new Set()}
                  slotMap={{}}
                  onDateClick={() => { }}
                  isDateDisabled={() => true}
                />

                <div className="rounded-3xl p-5 border bg-blue-50 border-blue-100">
                  <p className="text-[10px] font-black uppercase tracking-widest mb-3 text-blue-655">
                    How to Submit
                  </p>
                  <p className="text-[11px] font-medium leading-relaxed text-blue-750">
                    Your previous schedule is shown here for your reference. To submit your new availability:
                  </p>
                  <ol className="list-decimal pl-4 mt-2 text-[11px] font-medium text-blue-750 space-y-1">
                    <li>Switch to the <strong>Weekly Schedule</strong> tab.</li>
                    <li>Toggle the days of the week you are available.</li>
                    <li>Configure times and click the submit button.</li>
                  </ol>
                </div>
              </div>
            </div>
          )}

          {/* Submit Action or Redirect Button */}
          {activeTab === 'schedule' ? (
            <button
              type="submit"
              disabled={loading || !token}
              className="w-full bg-[#143E78] hover:bg-[#0f2c56] text-white py-4 sm:py-4.5 rounded-2xl font-black text-sm sm:text-lg shadow-xl shadow-blue-900/10 hover:shadow-blue-900/20 hover:scale-[1.01] active:scale-[0.99] transition-all disabled:opacity-50 flex items-center justify-center gap-2 sm:gap-3 cursor-pointer px-2 text-center"
            >
              <Save className="w-[18px] h-[18px] sm:w-5 sm:h-5 flex-shrink-0" />
              <span>{loading ? 'Submitting...' : 'SUBMIT AVAILABILITY SCHEDULE'}</span>
            </button>
          ) : (
            previousSchedule && previousSchedule.some(d => d.isAvailable) && (
              <button
                type="button"
                onClick={() => setActiveTab('schedule')}
                className="w-full bg-gradient-to-r from-[#143E78] to-blue-500 hover:from-[#0f2c56] hover:to-[#1a52a0] text-white py-4 sm:py-4.5 rounded-2xl font-black text-xs sm:text-lg shadow-xl shadow-blue-900/10 hover:shadow-blue-900/20 hover:scale-[1.01] active:scale-[0.99] transition-all flex flex-col sm:flex-row items-center justify-center gap-1.5 sm:gap-3 cursor-pointer px-2 text-center"
              >
                <Clock className="w-[16px] h-[16px] sm:w-5 sm:h-5 flex-shrink-0" />
                <span>CONFIGURE NEW AVAILABILITY FOR THIS WEEK</span>
              </button>
            )
          )}
        </form>
      </div>
    </div>
  );
};

export default DoctorAvailabilityPublic;
